import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useLanguage } from '@/hooks/useLanguage';
import { normalizeLanguageCode } from '@/lib/language';
import { requestGetLocationSuggestions, LocationSuggestionsError, } from '@/api/network/locationSuggestions/methods/requestGetLocationSuggestions';
function getReadableErrorMessage(error) {
    if (error instanceof LocationSuggestionsError) {
        if (error.code === 'RATE_LIMIT') {
            return {
                en: 'Too many requests. Please try again later.',
                de: 'Zu viele Anfragen. Bitte versuchen Sie es später erneut.',
                it: 'Troppe richieste. Riprova più tardi.',
            };
        }
        if (error.code === 'BAD_REQUEST') {
            return {
                en: 'Please refine your search query.',
                de: 'Bitte präzisieren Sie Ihre Suchanfrage.',
                it: 'Per favore, specifica meglio la ricerca.',
            };
        }
    }
    return {
        en: 'Location suggestions are temporarily unavailable.',
        de: 'Standortvorschläge sind vorübergehend nicht verfügbar.',
        it: 'I suggerimenti località sono temporaneamente non disponibili.',
    };
}
export function useLocationSuggestionsSearch(options = {}) {
    const { t } = useTranslation();
    const { currentLanguage } = useLanguage();
    const normalizedLanguage = normalizeLanguageCode(options.language)
        ?? normalizeLanguageCode(currentLanguage)
        ?? 'en';
    const normalizedMinLength = Math.max(1, Math.floor(options.minLength ?? 3));
    const normalizedDebounceMs = Math.max(100, Math.floor(options.debounceMs ?? 350));
    const normalizedLimit = Math.max(1, Math.min(8, Math.floor(options.limit ?? 5)));
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [activeIndex, setActiveIndex] = useState(-1);
    const requestSeqRef = useRef(0);
    const abortRef = useRef(null);
    const hasTypedEnough = query.trim().length >= normalizedMinLength;
    const reset = useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;
        setQuery('');
        setLoading(false);
        setError(null);
        setSuggestions([]);
        setActiveIndex(-1);
    }, []);
    useEffect(() => {
        return () => {
            abortRef.current?.abort();
        };
    }, []);
    useEffect(() => {
        if (!hasTypedEnough) {
            abortRef.current?.abort();
            abortRef.current = null;
            setLoading(false);
            setError(null);
            setSuggestions([]);
            setActiveIndex(-1);
            return undefined;
        }
        const timer = window.setTimeout(() => {
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            const requestSeq = requestSeqRef.current + 1;
            requestSeqRef.current = requestSeq;
            setLoading(true);
            setError(null);
            requestGetLocationSuggestions({
                q: query.trim(),
                country: options.country,
                language: normalizedLanguage,
                limit: normalizedLimit,
                signal: controller.signal,
            })
                .then((results) => {
                if (requestSeqRef.current !== requestSeq) {
                    return;
                }
                setSuggestions(results);
                setActiveIndex(results.length > 0 ? 0 : -1);
                setLoading(false);
                setError(null);
            })
                .catch((requestError) => {
                if (requestSeqRef.current !== requestSeq) {
                    return;
                }
                if (requestError instanceof DOMException && requestError.name === 'AbortError') {
                    return;
                }
                setSuggestions([]);
                setActiveIndex(-1);
                setLoading(false);
                setError(t(getReadableErrorMessage(requestError)));
            });
        }, normalizedDebounceMs);
        return () => {
            window.clearTimeout(timer);
        };
    }, [
        hasTypedEnough,
        normalizedDebounceMs,
        normalizedLanguage,
        normalizedLimit,
        options.country,
        query,
        t,
    ]);
    const canMoveSelection = suggestions.length > 0;
    const selectActiveSuggestion = useCallback(() => {
        if (!canMoveSelection || activeIndex < 0 || activeIndex >= suggestions.length) {
            return null;
        }
        return suggestions[activeIndex];
    }, [activeIndex, canMoveSelection, suggestions]);
    const moveNext = useCallback(() => {
        if (!canMoveSelection) {
            return;
        }
        setActiveIndex((prev) => {
            if (prev < 0) {
                return 0;
            }
            return (prev + 1) % suggestions.length;
        });
    }, [canMoveSelection, suggestions.length]);
    const movePrev = useCallback(() => {
        if (!canMoveSelection) {
            return;
        }
        setActiveIndex((prev) => {
            if (prev < 0) {
                return suggestions.length - 1;
            }
            return (prev - 1 + suggestions.length) % suggestions.length;
        });
    }, [canMoveSelection, suggestions.length]);
    const status = useMemo(() => {
        if (!hasTypedEnough) {
            return 'idle';
        }
        if (loading) {
            return 'loading';
        }
        if (error) {
            return 'error';
        }
        if (suggestions.length === 0) {
            return 'empty';
        }
        return 'results';
    }, [error, hasTypedEnough, loading, suggestions.length]);
    return {
        query,
        setQuery,
        loading,
        error,
        suggestions,
        activeIndex,
        setActiveIndex,
        hasTypedEnough,
        normalizedMinLength,
        status,
        reset,
        selectActiveSuggestion,
        moveNext,
        movePrev,
    };
}
